// Upload a new TestAudioFile.
//
// Flow:
//   1. User picks a file (accept .wav .mp3 .m4a .ogg .flac).
//   2. We probe size + duration via a hidden <audio> element. Rejection at
//      this stage avoids burning a multipart upload on a bad file.
//   3. On submit: multipart-upload with type="test_audio_source", then
//      POST /test-audio-files. The row appears in TRANSCODING status; the
//      TranscodeTestAudioWorkflow flips it to READY (or FAILED).
//
// Unlike the deleted BulkCreateThreadDialog, we DON'T immediately start a
// thread — the user picks "Run" from the list later.

import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";

import { uploadBlobMultipart } from "../services/audioUploader";
import { createTestAudioFile } from "../services/testAudioService";

const SUPPORTED_EXTENSIONS = ".wav,.mp3,.m4a,.ogg,.flac";
const SUPPORTED_MIME_PREFIXES = ["audio/"];
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB

// Probe a File's duration via a hidden <audio>. Returns ms or null.
// Exported for tests so they can stub the answer without touching jsdom.
export async function probeDurationMs(file) {
  if (!file) return null;
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const audio = new Audio();
      let settled = false;
      const cleanup = () => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* noop */
        }
      };
      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        if (settled) return;
        settled = true;
        const ms = Math.floor((audio.duration || 0) * 1000);
        cleanup();
        resolve(Number.isFinite(ms) && ms > 0 ? ms : null);
      };
      audio.onerror = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(null);
      };
      audio.src = url;
    } catch {
      resolve(null);
    }
  });
}

function isLikelyAudio(file) {
  if (!file) return false;
  const t = (file.type || "").toLowerCase();
  if (SUPPORTED_MIME_PREFIXES.some((p) => t.startsWith(p))) return true;
  // Some browsers/files come through with empty MIME — fall back to
  // extension (filter out the leading dot in SUPPORTED_EXTENSIONS).
  const name = (file.name || "").toLowerCase();
  return SUPPORTED_EXTENSIONS.split(",").some((ext) =>
    name.endsWith(ext.trim()),
  );
}

const UploadTestAudioDialog = ({
  isOpen,
  onClose,
  onUploaded,
  // Test injection points.
  uploadFn,
  createFn,
  probeFn,
}) => {
  const toast = useToast();
  const doUpload = uploadFn || uploadBlobMultipart;
  const doCreate = createFn || createTestAudioFile;
  const doProbe = probeFn || probeDurationMs;

  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setName("");
      setDescription("");
      setIsUploading(false);
      setProgress(0);
      setError(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen]);

  const handlePickFile = async (e) => {
    const picked = e.target.files?.[0] || null;
    setError(null);
    setFile(null);
    if (!picked) return;

    if (picked.size > MAX_BYTES) {
      setError(`File is ${Math.round(picked.size / (1024 * 1024))} MB — max is 500 MB.`);
      return;
    }
    if (!isLikelyAudio(picked)) {
      setError(
        "Unsupported file type. Use one of: .wav .mp3 .m4a .ogg .flac.",
      );
      return;
    }
    const ms = await doProbe(picked);
    if (ms == null || ms <= 0) {
      setError(
        "Could not read duration from this file. Try a different audio file.",
      );
      return;
    }
    setFile(picked);
    if (!name.trim()) {
      const stem = picked.name.replace(/\.[^.]+$/, "");
      setName(stem);
    }
  };

  const canSubmit = !isUploading && !!file && name.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsUploading(true);
    setError(null);
    setProgress(0);
    try {
      const uploadResult = await doUpload({
        uploadType: "test_audio_source",
        objectId: null,
        blob: file,
        fileName: file.name,
        setUploadProgress: setProgress,
        toastFn: (t) => toast(t),
        contentTypeOverride: file.type || undefined,
        maxRetriesPerPart: 3,
      });

      if (uploadResult.status !== "completed") {
        setError(
          uploadResult.errorMessage || "Upload did not complete. Please retry.",
        );
        return;
      }

      await doCreate({
        name: name.trim(),
        description: description.trim() || null,
        sourceS3Bucket: uploadResult.bucketName,
        sourceS3Key: uploadResult.filePath,
        sourceFilename: file.name,
        sourceMimeType: file.type || null,
      });

      toast({
        title: "Upload registered",
        description: "Transcoding will start shortly.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onUploaded?.();
    } catch (e) {
      const message =
        e?.response?.data?.detail || e?.message || "Upload failed.";
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isUploading ? () => {} : onClose} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="upload-test-audio-dialog">
        <ModalHeader>Upload Test Audio</ModalHeader>
        <ModalCloseButton isDisabled={isUploading} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Audio file</FormLabel>
              <Input
                ref={fileInputRef}
                type="file"
                accept={SUPPORTED_EXTENSIONS}
                onChange={handlePickFile}
                isDisabled={isUploading}
                data-testid="upload-file-input"
              />
              <FormHelperText>
                Up to 500 MB. .wav .mp3 .m4a .ogg .flac. Files are transcoded to 16kHz mono WAV.
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Toyota coaching call demo"
                isDisabled={isUploading}
                data-testid="upload-name-input"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes"
                rows={3}
                isDisabled={isUploading}
                data-testid="upload-description-input"
              />
            </FormControl>

            {isUploading && (
              <Box data-testid="upload-progress">
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" color="gray.600">
                    Uploading…
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {progress}%
                  </Text>
                </HStack>
                <Progress value={progress} size="xs" colorScheme="blue" />
              </Box>
            )}

            {error && (
              <Alert status="error" borderRadius="md" data-testid="upload-error">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            mr={3}
            onClick={onClose}
            variant="ghost"
            isDisabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={!canSubmit}
            isLoading={isUploading}
            data-testid="upload-submit-button"
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadTestAudioDialog;
